/**
 * Tenant settings hooks.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import {
  tenantSettingsApi,
  type PublicTenantBootstrap,
  type TenantSettings,
} from "@/lib/api/tenant-settings";

export const tenantSettingsKey = ["tenant-settings"];
export const publicTenantBootstrapKey = (host: string | null) => ["public-tenant-bootstrap", host];

function resolveCurrentTenantHost(): string | null {
  if (typeof window === "undefined") return null;
  return window.location.host || window.location.hostname || null;
}

export function useTenantSettings(options?: { enabled?: boolean }) {
  const { isAuthenticated, isLoading } = useAuth();

  return useQuery({
    queryKey: tenantSettingsKey,
    queryFn: () => tenantSettingsApi.getSettings(),
    staleTime: 60_000,
    enabled: (options?.enabled ?? true) && !isLoading && isAuthenticated,
    retry: false,
  });
}

export function usePublicTenantBootstrap(options?: { enabled?: boolean; host?: string | null }) {
  const { isAuthenticated, isLoading } = useAuth();
  const host = options?.host ?? resolveCurrentTenantHost();

  return useQuery<PublicTenantBootstrap>({
    queryKey: publicTenantBootstrapKey(host),
    queryFn: () => tenantSettingsApi.getPublicBootstrap(host ?? undefined),
    staleTime: 5 * 60_000,
    enabled: (options?.enabled ?? true) && !isLoading && !isAuthenticated,
    retry: false,
  });
}

export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: Partial<TenantSettings>) =>
      tenantSettingsApi.updateSettings(body),
    onSuccess: (data) => {
      queryClient.setQueryData(tenantSettingsKey, data);
    },
  });
}
